import { SheetTab } from './types'

function toCamel(s: string) {
  return s.replace(/([-_ ][a-z])/gi, ($1) => {
    return $1.toUpperCase().replaceAll(/[-_ ]/g, '')
  })
}

const SHEETS_ROOT = 'https://sheets.googleapis.com/v4/spreadsheets'

/** Safe fetch -> JSON with decent error messages (browser) */
async function fetchJson<T>(url: string, accessToken: string): Promise<T> {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  const text = await res.text()
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} – ${text.slice(0, 300)}`)
  }
  // Sheets returns JSON; be defensive anyway:
  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error(`Failed to parse JSON (length=${text.length})`)
  }
}

export async function listTabs(
  spreadsheetId: string,
  accessToken: string,
): Promise<SheetTab[]> {
  const url =
    `${SHEETS_ROOT}/${encodeURIComponent(spreadsheetId)}` +
    `?fields=sheets(properties(sheetId%2Ctitle))`

  const json = await fetchJson<{
    sheets?: { properties?: { sheetId?: number; title?: string } }[]
  }>(url, accessToken)

  return (json.sheets ?? [])
    .map((s) => ({
      sheetId: s.properties?.sheetId as number,
      title: s.properties?.title as string,
    }))
    .filter((t) => Number.isFinite(t.sheetId) && !!t.title)
}

/**
 * Get values for a whole tab by TITLE (recommended; Values API is small).
 * If you pass only the title (no range), Sheets returns the used range.
 */
export async function getTabValuesByTitle(p: {
  spreadsheetId: string | number
  title: string
  accessToken: string
  rangeA1?: string // e.g. "A1:Z200". If omitted, whole used range of the tab.
}): Promise<{ range: string; majorDimension: string; values?: string[][] }> {
  const { spreadsheetId, title, accessToken, rangeA1 } = p
  const a1 = rangeA1
    ? `${a1EscapeSheetName(title)}!${rangeA1}`
    : a1EscapeSheetName(title)
  const url =
    `${SHEETS_ROOT}/${encodeURIComponent(spreadsheetId)}` +
    `/values/${encodeURIComponent(a1)}`

  return fetchJson(url, accessToken)
}

/**
 * Escape a sheet name for use in A1 notation.
 * Sheet names with spaces or special characters must be wrapped in single quotes,
 * and any single quotes within the name must be doubled.
 */
export function a1EscapeSheetName(title: string): string {
  // Escape internal single quotes by doubling them, then wrap in single quotes
  return `'${title.replace(/'/g, "''")}'`
}

export function parseSheetForConfig<T>(
  sheet: string[][],
  config: { header: string; key?: string }[],
): T[] {
  if (!sheet.length || !config.length) return []
  const normalize = (s: string) => s.trim().toLowerCase()

  const headers = config.map(({ header }) => normalize(header))
  const headerRow = sheet.findIndex((row) => {
    const normalizedRow = row.map(normalize)
    return headers.every((h) => normalizedRow.includes(h))
  })
  if (headerRow < 0) return []
  const keyMap = config.reduce(
    (acc, { header, key }) => {
      const objKey = key ?? toCamel(header.toLowerCase())
      acc[objKey] = sheet[headerRow].findIndex(
        (h) => normalize(h) === normalize(header),
      )
      return acc
    },
    {} as Record<string, number>,
  )
  return sheet.splice(headerRow + 1).reduce<T[]>((acc, row) => {
    const newObj = Object.entries(keyMap).reduce((acc, [key, index]) => {
      return {
        ...acc,
        [key]: row[index],
      }
    }, {})
    if (Object.values(newObj).every((v) => !v)) return acc
    return [...acc, newObj as T]
  }, [])
}
